import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  Dialog,
  DialogContent,
  DialogTitle,
} from '@ccp/ui';
import { useNavigate } from '@tanstack/react-router';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { shortcutById, shortcutLabel } from '../../lib/keyboard/shortcut-map.js';
import { useShortcuts } from '../../lib/keyboard/use-shortcuts.js';
import { usePermission } from '../../session/use-permission.js';
import { useContactSearch } from '../contacts/api.js';
import { type PaletteAction, paletteGroups, useCommandActionList } from './registry.js';

export type { PaletteAction } from './registry.js';

export interface CommandPaletteProps {
  readonly open: boolean;
  readonly onOpenChange: (open: boolean) => void;
  /** The shell's own actions. What the screens can do comes from the registry. */
  readonly actions: readonly PaletteAction[];
}

/** How long after the last keystroke a search is sent. */
const searchDebounceMs = 250;

/** Below this many characters, nothing is searched: one letter is every contact in the tenant. */
const minimumSearch = 2;

/**
 * The keyboard path to everything the workspace can do.
 *
 * Two sources, for the same reason the agent's screen has two: the shell's own actions are passed
 * in, and whatever is **on screen** publishes itself through the registry. So "transfer this call"
 * is offered while there is a call and not otherwise, and the palette never imports the features it
 * offers.
 *
 * A third source is neither: **contacts are searched on the server**. The list is filtered locally
 * by cmdk once it is here, but a tenant's contacts are not in the browser to be filtered, so what
 * the person types past two characters is sent to `/v1/contacts` — debounced, and only if they may
 * read contacts at all.
 *
 * Every row shows its keys where it has them, because a palette is where somebody learns the
 * keyboard. The map is the single source of both (`lib/keyboard/shortcut-map.ts`).
 */
export function CommandPalette({ open, onOpenChange, actions }: CommandPaletteProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const fromScreens = useCommandActionList();
  const maySearchContacts = usePermission('contacts.read');
  const [search, setSearch] = useState('');
  const [query, setQuery] = useState('');

  // `whileTyping`, and only here: being able to open the palette from inside a search field is the
  // point of having it.
  useShortcuts('global', { 'global.palette': () => onOpenChange(!open) }, { whileTyping: true });

  // Debounced, so a search is a request per pause rather than per keystroke.
  useEffect(() => {
    const trimmed = search.trim();
    if (trimmed.length < minimumSearch) {
      setQuery('');
      return;
    }
    const timer = setTimeout(() => setQuery(trimmed), searchDebounceMs);
    return () => clearTimeout(timer);
  }, [search]);

  // Closing forgets what was typed: a palette that reopened with yesterday's search would be a
  // palette somebody has to clear before they can use it.
  useEffect(() => {
    if (!open) {
      setSearch('');
      setQuery('');
    }
  }, [open]);

  const contacts = useContactSearch(maySearchContacts ? query : '');
  const all = [...actions, ...fromScreens];

  const choose = (action: PaletteAction) => {
    onOpenChange(false);
    action.run();
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent size="md" className="p-(--space-none)">
        <DialogTitle className="sr-only">{t('palette.title')}</DialogTitle>
        <Command label={t('palette.title')} shouldFilter>
          <CommandInput
            placeholder={t('palette.placeholder')}
            value={search}
            onValueChange={setSearch}
          />
          <CommandList>
            <CommandEmpty>{t('palette.empty')}</CommandEmpty>

            {paletteGroups.map((group) => {
              const rows = all.filter((action) => action.group === group);
              if (rows.length === 0) {
                return null;
              }
              return (
                <CommandGroup key={group} heading={t(`palette.group.${group}`)}>
                  {rows.map((action) => {
                    const shortcut =
                      action.shortcutId === undefined ? undefined : shortcutById(action.shortcutId);
                    return (
                      <CommandItem
                        key={action.id}
                        value={action.label}
                        onSelect={() => choose(action)}
                      >
                        <span className="flex-1">{action.label}</span>
                        {shortcut === undefined ? null : (
                          <kbd className="rounded-(--radius-sm) border border-(--color-border) px-(--space-xs) font-mono text-(length:--font-size-caption) text-(--color-text-muted)">
                            {shortcutLabel(shortcut)}
                          </kbd>
                        )}
                      </CommandItem>
                    );
                  })}
                </CommandGroup>
              );
            })}

            {(contacts.data ?? []).length === 0 ? null : (
              <CommandGroup heading={t('palette.group.contacts')}>
                {(contacts.data ?? []).map((contact) => (
                  <CommandItem
                    key={contact.id}
                    // The typed text is already the server's filter, so cmdk must not filter these
                    // again: a contact whose name the search matched on an identity rather than a
                    // name would be fetched and then hidden.
                    value={`${search} ${contact.displayName}`}
                    onSelect={() => {
                      onOpenChange(false);
                      void navigate({
                        to: '/contacts/$contactId',
                        params: { contactId: contact.id },
                      });
                    }}
                  >
                    {contact.displayName}
                  </CommandItem>
                ))}
              </CommandGroup>
            )}
          </CommandList>
        </Command>
      </DialogContent>
    </Dialog>
  );
}
